/**
 * Standard MIDI File writer for Bandmate (SPEC §4.6, panel `[music-19]`).
 *
 * Pure: `buildSMF(session)` turns a {@link Session} into the bytes of a
 * format-1 SMF at 480 PPQ (120 ticks per 16th step) with five tracks:
 *
 * | # | content                                   | status |
 * |---|-------------------------------------------|--------|
 * | 0 | track name, tempo, 4/4 time signature      | —      |
 * | 1 | drums (GM notes from `DRUM_GM_NOTE`)      | `0x99` |
 * | 2 | bass                                      | `0x90` |
 * | 3 | chords                                    | `0x91` |
 * | 4 | lead                                      | `0x92` |
 *
 * Swing is baked in (odd 16th steps shift by `round(swing/100 × 40)` ticks),
 * velocities become `max(1, round(v × 127))`, notes that run past the 64-step
 * loop are clipped at the loop end, and muted tracks are exported anyway (the
 * mix state belongs to the DAW). No download is triggered here — the UI wraps
 * the bytes in a blob URL.
 *
 * Two details keep the file clean when it lands in a DAW:
 * - A pitch never sounds twice at once on one channel: an earlier note of the
 *   same pitch is cut at the next note-on, and duplicates at the same tick are
 *   merged (louder velocity, longer length). MIDI has no "retrigger while
 *   held", so this is the only unambiguous rendering.
 * - Every track's end-of-track sits at the loop end (tick 7680), so the file
 *   imports as an exact 4-bar clip even when a track is empty or ends early.
 */
import type { DrumHit, Note, Session } from '../types';
import { DRUM_GM_NOTE, SWING_MAX, SWING_MIN, TOTAL_STEPS } from '../types';

/** Pulses (ticks) per quarter note written into the header. */
export const PPQ = 480;

/** Ticks per 16th step: a quarter is four steps. */
export const TICKS_PER_STEP = PPQ / 4;

/** Absolute tick of the loop end; note-offs are clipped here. */
export const LOOP_END_TICKS = TOTAL_STEPS * TICKS_PER_STEP;

/** Ticks a drum hit is held before its note-off (GM drums ignore it, but a length is required). */
export const DRUM_HIT_TICKS = TICKS_PER_STEP;

/** Maximum swing shift in ticks at swing 100 (a 16th's worth at 100 % would be 120; we use MPC-style ~1/3). */
const SWING_FULL_TICKS = 40;

/** MIDI channel (0-based) per instrument track. Drums are channel 10 (index 9). */
const CHANNEL = { drums: 9, bass: 0, chords: 1, lead: 2 } as const;

const META_TRACK_NAME = 0x03;
const META_END_OF_TRACK = 0x2f;
const META_TEMPO = 0x51;
const META_TIME_SIGNATURE = 0x58;

/** Default name written into track 0 when `opts.name` is not given. */
export const DEFAULT_SMF_NAME = 'Bandmate';

/**
 * Encode a non-negative integer as an SMF variable-length quantity
 * (7 bits per byte, big-endian, continuation bit set on all but the last byte).
 *
 * @throws RangeError for negative, non-integer, or values above `0x0FFFFFFF`.
 */
export function encodeVarLen(n: number): number[] {
  if (!Number.isInteger(n) || n < 0 || n > 0x0fffffff) {
    throw new RangeError(`variable-length quantity out of range: ${n}`);
  }
  const out: number[] = [n & 0x7f];
  let rest = n >>> 7;
  while (rest > 0) {
    out.unshift((rest & 0x7f) | 0x80);
    rest >>>= 7;
  }
  return out;
}

/**
 * The three data bytes of an `FF 51 03` tempo meta event: microseconds per
 * quarter note, `round(60_000_000 / bpm)`, big-endian.
 *
 * @example tempoBytes(82) → [0x0B, 0x2A, 0x3B]; tempoBytes(120) → [0x07, 0xA1, 0x20]
 * @throws RangeError when `bpm` is not a positive finite number.
 */
export function tempoBytes(bpm: number): [number, number, number] {
  if (!Number.isFinite(bpm) || bpm <= 0) throw new RangeError(`invalid tempo: ${bpm}`);
  const us = Math.min(0xffffff, Math.round(60_000_000 / bpm));
  return [(us >>> 16) & 0xff, (us >>> 8) & 0xff, us & 0xff];
}

/**
 * Tick shift applied to a step for a given swing amount: odd 16th steps
 * (the "e" and "a" of each beat) are delayed by `round(swing / 100 × 40)`
 * ticks, even steps are untouched. `swing` is clamped into
 * `SWING_MIN..SWING_MAX` (0..60) so a corrupt session can never push a step
 * past its neighbour; non-finite swing counts as 0.
 */
export function swingOffsetTicks(step: number, swing: number): number {
  if (!Number.isFinite(step) || Math.abs(step) % 2 !== 1) return 0;
  const amount = Number.isFinite(swing) ? Math.min(SWING_MAX, Math.max(SWING_MIN, swing)) : 0;
  return Math.round((amount / 100) * SWING_FULL_TICKS);
}

/** MIDI velocity byte for a 0..1 velocity: never silent, never above 127. */
export function velocityByte(v: number): number {
  const x = Number.isFinite(v) ? v : 0;
  return Math.max(1, Math.min(127, Math.round(x * 127)));
}

/** A channel event with an absolute tick, before delta encoding. */
interface TimedEvent {
  tick: number;
  /** Note-offs sort before note-ons at the same tick. */
  off: boolean;
  /** Insertion index, for a stable sort. */
  seq: number;
  status: number;
  data1: number;
  data2: number;
}

/** Wrap any integer step into 0..63 (defensive; sessions should already comply). */
function wrapStep(step: number): number {
  const s = Math.floor(Number.isFinite(step) ? step : 0);
  return ((s % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
}

function clampPitch(p: number): number {
  const x = Number.isFinite(p) ? Math.round(p) : 0;
  return Math.max(0, Math.min(127, x));
}

/** One sounding note on a channel, in absolute ticks, before overlap resolution. */
interface Voice {
  on: number;
  off: number;
  pitch: number;
  velocity: number;
  /** Insertion index, for a stable sort. */
  seq: number;
}

/**
 * Build a voice for one note: the note-on carries the swing shift; the
 * note-off follows it by `lengthTicks` but never past the loop end, and
 * always at least one tick after the note-on.
 */
function voice(seq: number, step: number, swing: number, pitch: number, velocity: number, lengthTicks: number): Voice {
  const s = wrapStep(step);
  const on = s * TICKS_PER_STEP + swingOffsetTicks(s, swing);
  const off = Math.max(on + 1, Math.min(LOOP_END_TICKS, on + Math.max(1, lengthTicks)));
  return { on, off, pitch: clampPitch(pitch), velocity: velocityByte(velocity), seq };
}

/**
 * Make each pitch monophonic on its channel. Voices are visited in (on, seq)
 * order; for two voices of the same pitch, an exact same-tick duplicate is
 * merged into the first (max velocity, max length), and an overlap cuts the
 * earlier voice at the later voice's note-on. Returns a new array; input
 * voices are not modified.
 */
function resolveOverlaps(voices: readonly Voice[]): Voice[] {
  const ordered = voices
    .map((v) => ({ ...v }))
    .sort((a, b) => (a.on !== b.on ? a.on - b.on : a.seq - b.seq));
  const last = new Map<number, Voice>();
  const out: Voice[] = [];
  for (const v of ordered) {
    const prev = last.get(v.pitch);
    if (prev && prev.on === v.on) {
      prev.off = Math.max(prev.off, v.off);
      prev.velocity = Math.max(prev.velocity, v.velocity);
      continue;
    }
    if (prev && v.on < prev.off) prev.off = v.on;
    last.set(v.pitch, v);
    out.push(v);
  }
  return out;
}

/** Expand resolved voices into note-on / note-off channel events. */
function voiceEvents(voices: readonly Voice[], channel: number): TimedEvent[] {
  const events: TimedEvent[] = [];
  for (const v of voices) {
    events.push({ tick: v.on, off: false, seq: events.length, status: 0x90 | channel, data1: v.pitch, data2: v.velocity });
    events.push({ tick: v.off, off: true, seq: events.length, status: 0x80 | channel, data1: v.pitch, data2: 0 });
  }
  return events;
}

function melodicEvents(notes: readonly Note[], channel: number, swing: number): TimedEvent[] {
  const voices = notes.map((n, i) => {
    const lengthSteps = Number.isFinite(n.length) ? Math.max(1, Math.round(n.length)) : 1;
    return voice(i, n.step, swing, n.pitch, n.velocity, lengthSteps * TICKS_PER_STEP);
  });
  return voiceEvents(resolveOverlaps(voices), channel);
}

function drumEvents(hits: readonly DrumHit[], swing: number): TimedEvent[] {
  const voices: Voice[] = [];
  for (const h of hits) {
    const gm = DRUM_GM_NOTE[h.lane];
    if (gm === undefined) continue; // unknown lane in a hand-edited session: skip rather than emit garbage
    voices.push(voice(voices.length, h.step, swing, gm, h.velocity, DRUM_HIT_TICKS));
  }
  return voiceEvents(resolveOverlaps(voices), CHANNEL.drums);
}

/** Sort by absolute tick; at equal ticks note-offs first; otherwise insertion order. */
function sortEvents(events: TimedEvent[]): TimedEvent[] {
  return events.slice().sort((a, b) => {
    if (a.tick !== b.tick) return a.tick - b.tick;
    if (a.off !== b.off) return a.off ? -1 : 1;
    return a.seq - b.seq;
  });
}

function metaEvent(delta: number, type: number, data: ArrayLike<number>): number[] {
  return [...encodeVarLen(delta), 0xff, type, ...encodeVarLen(data.length), ...Array.from(data)];
}

function trackNameEvent(name: string): number[] {
  return metaEvent(0, META_TRACK_NAME, new TextEncoder().encode(name));
}

/**
 * Serialise a full MTrk chunk: header, length, the given event bytes, and an
 * end-of-track placed at the loop end (`lastTick` is the absolute tick of the
 * last event already in `body`, so the EOT delta is `LOOP_END_TICKS - lastTick`).
 */
function trackChunk(body: number[], lastTick: number): number[] {
  const withEot = [...body, ...metaEvent(LOOP_END_TICKS - lastTick, META_END_OF_TRACK, [])];
  const len = withEot.length;
  return [
    0x4d, 0x54, 0x72, 0x6b, // 'MTrk'
    (len >>> 24) & 0xff, (len >>> 16) & 0xff, (len >>> 8) & 0xff, len & 0xff,
    ...withEot,
  ];
}

/** Track name followed by every channel event, delta-encoded in tick order. */
function instrumentTrack(name: string, events: TimedEvent[]): number[] {
  const body = trackNameEvent(name);
  let last = 0;
  for (const e of sortEvents(events)) {
    body.push(...encodeVarLen(e.tick - last), e.status, e.data1 & 0x7f, e.data2 & 0x7f);
    last = e.tick;
  }
  return trackChunk(body, last);
}

/** Track 0: name, tempo, 4/4 time signature (24 clocks per metronome click, 8 32nds per quarter). */
function conductorTrack(name: string, tempo: number): number[] {
  const body = [
    ...trackNameEvent(name),
    ...metaEvent(0, META_TEMPO, tempoBytes(tempo)),
    ...metaEvent(0, META_TIME_SIGNATURE, [0x04, 0x02, 0x18, 0x08]),
  ];
  return trackChunk(body, 0);
}

/** Options for {@link buildSMF}. */
export interface BuildSMFOptions {
  /** Sequence name written into track 0 (defaults to `"Bandmate"`). */
  name?: string;
}

/**
 * Build a format-1 Standard MIDI File from a session.
 *
 * Header is always `4D 54 68 64 00 00 00 06 00 01 00 05 01 E0` (format 1,
 * 5 tracks, 480 PPQ). Every track ends with `FF 2F 00` at the loop end
 * (tick 7680). The session is not modified and the output is deterministic.
 *
 * @param session the committed (or transient) session to export
 * @param opts optional sequence name for track 0 (trimmed; blank → `"Bandmate"`)
 * @returns the complete file bytes, ready to wrap in a `Blob` of type `audio/midi`
 * @throws RangeError when `session.tempo` is not a positive finite number
 */
export function buildSMF(session: Session, opts: BuildSMFOptions = {}): Uint8Array {
  const trimmed = (opts.name ?? '').trim();
  const name = trimmed.length > 0 ? trimmed : DEFAULT_SMF_NAME;
  const swing = session.swing;
  const { drums, bass, chords, lead } = session.tracks;

  const header = [
    0x4d, 0x54, 0x68, 0x64, // 'MThd'
    0x00, 0x00, 0x00, 0x06, // header length 6
    0x00, 0x01, // format 1
    0x00, 0x05, // 5 tracks
    (PPQ >>> 8) & 0xff, PPQ & 0xff, // division 480 = 0x01E0
  ];

  const bytes = [
    ...header,
    ...conductorTrack(name, session.tempo),
    ...instrumentTrack('Drums', drumEvents(drums.hits, swing)),
    ...instrumentTrack('Bass', melodicEvents(bass.notes, CHANNEL.bass, swing)),
    ...instrumentTrack('Chords', melodicEvents(chords.notes, CHANNEL.chords, swing)),
    ...instrumentTrack('Lead', melodicEvents(lead.notes, CHANNEL.lead, swing)),
  ];
  return Uint8Array.from(bytes);
}
